import { useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DataGridV2 } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column } from '@bloomays-lib/ui.shared/datagrid';
import { AnnualPrimeRowStatus, AnnualPrimeTableRow } from '../../types/primes';
import { formatPrimeEuroWithSign } from '../../utils/monthlyPrime.utils';

export type AnnualTamPrimesTableProps = {
  rows: AnnualPrimeTableRow[];
  loading: boolean;
};

const leftAlignedColumn = {
  align: 'left' as const,
  headerAlign: 'left' as const,
};

const getPrimeStatusDotColor = (status: AnnualPrimeRowStatus, theme: Theme) =>
  status === 'validated' ? theme.palette.green[100] : theme.palette.beige[100];

const AnnualTamPrimesTable = ({ rows, loading }: AnnualTamPrimesTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['primes']);

  const columns: DataGridV2Column<AnnualPrimeTableRow>[] = useMemo(
    () => [
      {
        field: 'monthYear',
        header: t('columnMonth'),
        minWidth: 120,
        flex: 0.8,
        variant: 'custom',
        render: (row) => (
          <MonthYearCell>
            <MonthYearText>{row.monthYear}</MonthYearText>
            <StatusDot
              $color={getPrimeStatusDotColor(row.status, theme)}
              title={row.status === 'validated' ? t('monthStatusValidated') : t('monthStatusPending')}
            />
          </MonthYearCell>
        ),
        ...leftAlignedColumn,
      },
      {
        field: 'actions',
        header: t('columnAction'),
        width: 72,
        sortable: false,
        filterable: false,
        variant: 'custom',
        render: () => (
          <IconButton size="small" disabled aria-label={t('annualActionDetailSoon')}>
            <SearchIcon fontSize="small" />
          </IconButton>
        ),
        ...leftAlignedColumn,
      },
      {
        field: 'totalFreelanceMarge',
        header: t('columnFreelanceMarge'),
        minWidth: 130,
        flex: 1,
        variant: 'money',
        money: (row) => row.totalFreelanceMarge,
        ...leftAlignedColumn,
      },
      {
        field: 'totalBillingMarge',
        header: t('columnBillingMarge'),
        minWidth: 120,
        flex: 1,
        variant: 'money',
        money: (row) => row.totalBillingMarge,
        ...leftAlignedColumn,
      },
      {
        field: 'totalMarge',
        header: t('columnTotalMarge'),
        minWidth: 120,
        flex: 1,
        variant: 'money',
        money: (row) => row.totalMarge,
        ...leftAlignedColumn,
      },
      {
        field: 'totalPrime',
        header: t('columnCommission'),
        minWidth: 120,
        flex: 1,
        variant: 'money',
        money: (row) => row.totalPrime,
        ...leftAlignedColumn,
      },
      {
        field: 'bonus',
        header: t('columnBonus'),
        minWidth: 110,
        flex: 0.9,
        variant: 'money',
        money: (row) => row.bonus,
        ...leftAlignedColumn,
      },
      {
        field: 'regularisation',
        header: t('columnRegularisation'),
        minWidth: 140,
        flex: 1,
        valueGetter: (row) => formatPrimeEuroWithSign(row.regularisation),
        ...leftAlignedColumn,
      },
      {
        field: 'totalDeals',
        header: t('columnDeals'),
        minWidth: 100,
        flex: 0.7,
        ...leftAlignedColumn,
      },
      {
        field: 'totalMissions',
        header: t('columnMissions'),
        minWidth: 110,
        flex: 0.8,
        ...leftAlignedColumn,
      },
    ],
    [t, theme],
  );

  return (
    <DataGridV2<AnnualPrimeTableRow>
      uniqueId="annual-tam-primes-table"
      rows={rows}
      columns={columns}
      loading={loading}
      loadingVariant="bar"
      getRowId={(row) => row.rowId}
      search={false}
      enableExport={false}
      enableColumnSelector={false}
      enableFilterPanel={false}
      pagination={rows.length > 12}
      pageSize={12}
      hideFooter={rows.length <= 12}
    />
  );
};

export default AnnualTamPrimesTable;

const MonthYearCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const MonthYearText = styled('span')(() => ({
  fontSize: 13,
  whiteSpace: 'nowrap',
}));

const StatusDot = styled('span', {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color: string }>(({ $color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: $color,
}));
